import { type AppContext } from "~/lib/context";
import { type GetMarketDataStatsResponse, type Input } from "~/pages/api/get-market-data-stats";
import { type NullableAuthenticatedUser } from "~/services/auth/fetchAuthenticatedUser";
import { computeMarketDataByLevel } from "~/services/market-data/dataset-employee/compute-compensations/computeMarketDataByLevel";
import { fetchDatasetEmployeesForMarketData } from "~/services/market-data/market-data-input/fetchDatasetEmployeesForMarketData";
import { formatMarketDataInput } from "~/services/market-data/market-data-input/formatMarketDataInput";

export const getMarketDataStats = async (
  ctx: AppContext,
  input: Input,
  user: NullableAuthenticatedUser
): Promise<GetMarketDataStatsResponse> => {
  const marketDataParams = await formatMarketDataInput(ctx, { input, user });

  const { companyEmployees, datasetEmployeesWithFilters } = await fetchDatasetEmployeesForMarketData(ctx, {
    user,
    options: {
      ...marketDataParams,
      showCompanyEmployees: input.showCompanyEmployees,
      mergeAdvancedLevels: input.mergeAdvancedLevels,
      useTestData: input.useTestData,
      locationIds: input.market.locationsIds,
      employeeFiltersOptions: marketDataParams.employeesWithFiltersOptions,
      marketJobIds: marketDataParams.marketJobsIds,
    },
  });

  //NB: we drill the input for Figures AI at 'Employees' level
  const marketDataByLevel = await computeMarketDataByLevel(ctx, {
    levels: marketDataParams.marketDataLevels,
    benchmarkLevelsRange: marketDataParams.benchmarkLevelsRange,
    datasetEmployees: datasetEmployeesWithFilters,
    currency: marketDataParams.currency,
    weightedJobs: marketDataParams.marketWeightedJobs,
    company: marketDataParams.company ?? undefined,
    locationIds: input.market.locationsIds,
    percentiles: input.percentiles,
    industry: marketDataParams.marketIndustryTag,
    headcount: input.market.headcount,
    fundingRounds: input.market.fundingRounds,
    mergeAdvancedLevels: input.mergeAdvancedLevels,
  });

  const stats: GetMarketDataStatsResponse = {
    currency: marketDataParams.currency,
    suggestedCurrencies: marketDataParams.suggestedCurrencies,
    percentiles: input.percentiles,
    market: {
      byLevel: marketDataByLevel,
    },
  };

  if (companyEmployees) {
    companyEmployees.forEach((employee) => {
      const levelKey = ctx.featureFlags.CAN_ACCESS_LEVEL_FRAMEWORKS ? employee.benchmarkLevel?.id : employee.level;

      stats.market.byLevel[levelKey]?.companyEmployees.push(employee);
    });
  }

  //Compare to a second job function
  if (input.compare && marketDataParams.compareParams) {
    const { companyEmployees, datasetEmployeesWithFilters: comparedDatasetEmployeesWithFilters } =
      await fetchDatasetEmployeesForMarketData(ctx, {
        user,
        options: {
          ...marketDataParams,
          showCompanyEmployees: input.showCompanyEmployees,
          mergeAdvancedLevels: input.mergeAdvancedLevels,
          useTestData: input.useTestData,
          locationIds: input.compare.locationsIds,
          employeeFiltersOptions: marketDataParams.compareParams.comparedEmployeesWithFiltersOptions,
          marketJobIds: marketDataParams.compareParams.compareJobsIds,
        },
      });

    if (
      comparedDatasetEmployeesWithFilters &&
      marketDataParams.compareParams.compareWeightedJobs &&
      input.compare.jobsIds.length
    ) {
      stats.comparison = {
        byLevel: await computeMarketDataByLevel(ctx, {
          levels: input.levels,
          benchmarkLevelsRange: marketDataParams.benchmarkLevelsRange,
          locationIds: input.compare.locationsIds,
          datasetEmployees: comparedDatasetEmployeesWithFilters,
          currency: marketDataParams.currency,
          weightedJobs: marketDataParams.compareParams.compareWeightedJobs,
          percentiles: input.percentiles,
          company: marketDataParams.company ?? undefined,
          headcount: input.compare.headcount,
          industry: marketDataParams.compareParams.comparedMarketIndustryTag,
          fundingRounds: input.compare.fundingRounds,
          mergeAdvancedLevels: input.mergeAdvancedLevels,
        }),
      };
    }

    if (companyEmployees) {
      companyEmployees.forEach((employee) => {
        const levelKey = ctx.featureFlags.CAN_ACCESS_LEVEL_FRAMEWORKS ? employee.benchmarkLevel?.id : employee.level;

        stats.comparison?.byLevel[levelKey]?.companyEmployees.push(employee);
      });
    }
  }

  return stats;
};
